// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  AdvisoryInstitution,
  GetAdvisoryAccountsTransactionsResponseBody,
} from '@shared/data-access/types/brokerage-types';
import { type AccountsTransactionsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';

/**
 * Builds accounts transactions data by enriching raw transaction data with
 * institution information.
 *
 * This function transforms the raw API response into a structured format that
 * includes institution names and initializes local_price fields for currency
 * conversion. It creates a lookup map for efficient O(1) institution retrieval and
 * maps each account's transactions with the corresponding institution metadata.
 *
 * @param accountsTransactions - The raw accounts transactions response from the
 *   API.
 * @param institutions - Array of institutions containing metadata (name, id) for
 *   enrichment.
 * @returns An array of enriched accounts transactions data with institution
 *   information and initialized local_price fields.
 */
export const buildAccountsTransactionsData = (
  accountsTransactions: GetAdvisoryAccountsTransactionsResponseBody,
  institutions: AdvisoryInstitution[],
): AccountsTransactionsData[] => {
  const institutionMap = new Map(
    institutions.map(institution => [institution.id, institution]),
  );

  return accountsTransactions.map(account => {
    const institution = institutionMap.get(account.institution_id);

    return {
      ...account,
      institution_id: account.institution_id,
      institution_name: institution?.name ?? '',
      iban: account.iban,
      transactions: account.transactions.map(transaction => ({
        ...transaction,
        institution_name: institution?.name ?? '',
        institution_id: account.institution_id,
        iban: account.iban,
        local_price: transaction.price,
        currency:
          transaction.currency ??
          institution?.products.accounts.find(
            product => product.iban === account.iban,
          )?.currency,
      })),
    };
  });
};
